import React, { useState } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Plus,
  Copy,
  CheckCircle,
  Coins,
  Badge as BadgeIcon,
  Tag,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  supabase,
  PlatformSetting,
  Voucher,
} from "@/integrations/supabase/client";

export const VoucherManagement = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [voucherCode, setVoucherCode] = useState("");
  const [voucherType, setVoucherType] = useState<string>("coins");
  const [voucherAmount, setVoucherAmount] = useState(10);
  const [expiryDays, setExpiryDays] = useState(30);
  const [isCreating, setIsCreating] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Fetch platform settings
  const { data: settings } = useQuery({
    queryKey: ["platformSettings"],
    queryFn: async () => {
      try {
        const { data: settingsData, error } = (await supabase
          .from("platform_settings")
          .select("*")) as { data: PlatformSetting[] | null; error: any };

        if (error) throw error;

        const formattedSettings: Record<string, any> = {};
        settingsData?.forEach((setting: PlatformSetting) => {
          formattedSettings[setting.setting_key] = setting.setting_value;
        });

        return formattedSettings;
      } catch (error) {
        console.error("Error fetching platform settings:", error);
        return {};
      }
    },
  });

  // Fetch vouchers data with improved error handling
  const { data: vouchers = [], isLoading } = useQuery({
    queryKey: ["vouchers"],
    queryFn: async () => {
      try {
        const { data, error } = await supabase.from("vouchers").select("*");

        if (error) {
          console.error("Error fetching vouchers:", error);
          throw error;
        }

        console.log("Fetched vouchers:", data);
        return data || [];
      } catch (error) {
        console.error("Error in vouchers query:", error);
        return [];
      }
    },
  });

  // Update platform settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: any }) => {
      const { data, error } = (await supabase.from("platform_settings").upsert(
        {
          setting_key: key,
          setting_value: value,
        },
        { onConflict: "setting_key" }
      )) as { data: any; error: any };

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platformSettings"] });
      toast({
        title: "Settings updated",
        description: "Platform settings have been updated successfully.",
      });
    },
    onError: (error) => {
      console.error("Error updating settings:", error);
      toast({
        title: "Error",
        description: "Failed to update settings.",
        variant: "destructive",
      });
    },
  });

  // Create voucher mutation
  const createVoucherMutation = useMutation({
    mutationFn: async (voucherData: any) => {
      const { data, error } = await supabase
        .from("vouchers")
        .insert(voucherData);

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vouchers"] });
      setVoucherCode("");
      toast({
        title: "Voucher created",
        description: "The voucher has been created successfully.",
      });
      setIsCreating(false);
    },
    onError: (error) => {
      console.error("Error creating voucher:", error);
      toast({
        title: "Error",
        description: "Failed to create voucher.",
        variant: "destructive",
      });
      setIsCreating(false);
    },
  });

  // Generate random voucher code
  const generateRandomCode = () => {
    const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let result = "";
    for (let i = 0; i < 8; i++) {
      result += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setVoucherCode(result);
  };

  // Copy voucher code to clipboard
  const copyToClipboard = (code: string, id: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);

      toast({
        title: "Copied to clipboard",
        description: `Voucher code ${code} copied to clipboard.`,
      });
    });
  };

  // Handle create voucher
  const handleCreateVoucher = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);

    if (!voucherCode.trim()) {
      toast({
        title: "Missing code",
        description: "Please generate or enter a voucher code.",
        variant: "destructive",
      });
      setIsCreating(false);
      return;
    }

    const expiryDate =
      expiryDays > 0
        ? new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000).toISOString()
        : null;

    try {
      const { data: userData } = await supabase.auth.getUser();

      createVoucherMutation.mutate({
        code: voucherCode.toUpperCase(),
        type: voucherType,
        amount: voucherAmount,
        expires_at: expiryDate,
        created_by: userData.user?.id,
      });
    } catch (error) {
      console.error("Error in handleCreateVoucher:", error);
      toast({
        title: "Error",
        description: "Failed to create voucher.",
        variant: "destructive",
      });
      setIsCreating(false);
    }
  };

  // Update signup credits
  const updateSignupCredits = (value: number) => {
    updateSettingsMutation.mutate({
      key: "signup_credits",
      value: { amount: value },
    });
  };

  // Update coin pricing
  const updateCoinPricing = (price: number) => {
    updateSettingsMutation.mutate({
      key: "coin_pricing",
      value: { price: price, currency: "EUR" },
    });
  };

  // Get the default signup credits
  const signupCredits = settings?.signup_credits?.amount || 5;

  // Get coin pricing
  const coinPrice = settings?.coin_pricing?.price || 1;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Platform Settings</CardTitle>
          <CardDescription>
            Configure default values for credits and coins
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="signup-credits">Default Signup Credits</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="signup-credits"
                  type="number"
                  value={signupCredits}
                  onChange={(e) => updateSignupCredits(Number(e.target.value))}
                  min={0}
                />
                <Button variant="outline" size="icon">
                  <CheckCircle className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-gray-500">
                Credits given to new users upon registration
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="coin-price">Coin Price (EUR)</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="coin-price"
                  type="number"
                  value={coinPrice}
                  onChange={(e) => updateCoinPricing(Number(e.target.value))}
                  min={0}
                  step={0.01}
                />
                <Button variant="outline" size="icon">
                  <CheckCircle className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-gray-500">Price per coin in EUR</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Create Voucher</CardTitle>
          <CardDescription>
            Generate vouchers for credits, coins, or premium features
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateVoucher} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="voucher-code">Voucher Code</Label>
                <div className="flex gap-2">
                  <Input
                    id="voucher-code"
                    value={voucherCode}
                    onChange={(e) =>
                      setVoucherCode(e.target.value.toUpperCase())
                    }
                    placeholder="Enter or generate code"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={generateRandomCode}
                  >
                    Generate
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="voucher-type">Voucher Type</Label>
                <Select value={voucherType} onValueChange={setVoucherType}>
                  <SelectTrigger id="voucher-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="coins">
                      <div className="flex items-center">
                        <Coins className="mr-2 h-4 w-4" />
                        <span>Coins</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="premium">
                      <div className="flex items-center">
                        <BadgeIcon className="mr-2 h-4 w-4" />
                        <span>Premium</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="credits">
                      <div className="flex items-center">
                        <Tag className="mr-2 h-4 w-4" />
                        <span>Free Credits</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="voucher-amount">Amount</Label>
                <Input
                  id="voucher-amount"
                  type="number"
                  value={voucherAmount}
                  onChange={(e) => setVoucherAmount(Number(e.target.value))}
                  min={1}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="expiry-days">Expires After (Days)</Label>
                <Input
                  id="expiry-days"
                  type="number"
                  value={expiryDays}
                  onChange={(e) => setExpiryDays(Number(e.target.value))}
                  min={0}
                  placeholder="0 = never expires"
                />
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={isCreating}>
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Voucher
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Vouchers</CardTitle>
          <CardDescription>Manage existing vouchers</CardDescription>
        </CardHeader>
        <CardContent className="overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : vouchers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No vouchers created yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Created By</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vouchers.map((voucher: any) => (
                    <TableRow key={voucher.id}>
                      <TableCell className="font-mono font-bold">
                        {voucher.code}
                      </TableCell>
                      <TableCell>
                        {voucher.type === "coins" && (
                          <Badge className="bg-amber-100 text-amber-800">
                            Coins
                          </Badge>
                        )}
                        {voucher.type === "premium" && (
                          <Badge className="bg-purple-100 text-purple-800">
                            Premium
                          </Badge>
                        )}
                        {voucher.type === "credits" && (
                          <Badge className="bg-blue-100 text-blue-800">
                            Free Credits
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{voucher.amount}</TableCell>
                      <TableCell>
                        {voucher.is_used ? (
                          <Badge className="bg-gray-100 text-gray-800">
                            Used
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-800">
                            Active
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {voucher.expires_at
                          ? new Date(voucher.expires_at).toLocaleDateString()
                          : "Never"}
                      </TableCell>
                      <TableCell>
                        {voucher.profiles?.username ||
                          voucher.profiles?.email ||
                          "Unknown"}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() =>
                            copyToClipboard(voucher.code, voucher.id)
                          }
                        >
                          {copiedId === voucher.id ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VoucherManagement;
